import { type FeedbackFormData, FeedbackSchema } from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconMessageCircleQuestion } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { InboxBell } from "@/components/notifications/inbox-bell";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { isModuleEnabled } from "@/lib/modules";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { useTeam } from "./providers/team";

type Item = {
	title: string;
	href: string | null;
};

type BreadcrumbsProps = {
	items: Item[];
};

export function Breadcrumbs({ items }: BreadcrumbsProps) {
	const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<FeedbackFormData>({
		resolver: zodResolver(FeedbackSchema),
		defaultValues: {
			message: "",
		},
	});

	const { teamID } = useTeam();

	const onSubmit = async (data: FeedbackFormData) => {
		try {
			const path = window.location.pathname;
			const response = await requestWithHandling<void>(() =>
				apiClient.api.feedbackCreate({
					message: data.message,
					team_id: teamID ?? undefined,
					path: path,
				}),
			);

			if (response.code !== "SUCCESS") {
				throw new Error(response.message);
			}
			toast.success("Feedback submitted", {
				description: "Thank you for your feedback!",
			});
			reset();
			setIsFeedbackDialogOpen(false);
		} catch (error) {
			toast.error("Failed to submit feedback", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		}
	};

	return (
		<header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
			<div className="flex items-center gap-2 px-4">
				<SidebarTrigger className="-ml-1" />
				<Separator orientation="vertical" className="mr-2 h-4" />
				<Breadcrumb>
					<BreadcrumbList>
						<BreadcrumbItem className="hidden md:block">
							<BreadcrumbLink href={items[0].href ?? undefined}>
								{items[0].title}
							</BreadcrumbLink>
						</BreadcrumbItem>
						{items.slice(1).map((crumb: Item, index: number) => (
							<Fragment key={crumb.title}>
								<BreadcrumbSeparator className="hidden md:block" />
								<BreadcrumbItem
									className={index < items.length - 2 ? "hidden md:block" : ""}
								>
									<BreadcrumbLink href={crumb.href ?? undefined}>
										{crumb.title}
									</BreadcrumbLink>
								</BreadcrumbItem>
							</Fragment>
						))}
					</BreadcrumbList>
				</Breadcrumb>
			</div>
			<div className="flex items-center gap-2 px-4 ml-auto">
				{isModuleEnabled("notifications") && <InboxBell />}
				{isModuleEnabled("feedback") && (
					<Dialog
						open={isFeedbackDialogOpen}
						onOpenChange={(open) => {
							setIsFeedbackDialogOpen(open);
							if (!open) {
								reset();
							}
						}}
					>
						<DialogTrigger
							className="focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-[3px] aria-invalid:ring-[3px] [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground size-7 p-0"
							title="Submit feedback"
						>
							<IconMessageCircleQuestion />
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Submit Feedback</DialogTitle>
								<DialogDescription>
									Share your thoughts or report an issue. We appreciate your
									feedback!
								</DialogDescription>
							</DialogHeader>
							<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
								<div className="space-y-4">
									<div className="space-y-1">
										<Textarea
											placeholder="What's on your mind?"
											{...register("message")}
											aria-invalid={errors.message ? "true" : "false"}
										/>
										{errors.message && (
											<p className="text-sm text-red-500">
												{errors.message.message}
											</p>
										)}
									</div>
								</div>
								<DialogFooter>
									<Button type="submit" disabled={isSubmitting}>
										Submit Feedback
									</Button>
								</DialogFooter>
							</form>
						</DialogContent>
					</Dialog>
				)}
			</div>
		</header>
	);
}
